import * as React from 'react';
import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { Menu, Transition } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';
import { Icon, SegmentedButtons, Slider, Typography } from '@scale/scaleui';
import cn from 'classnames';
import { useChatLayoutStore, ChatLayout } from './hooks/useChatLayoutStore';
import { Button } from 'design-system';

const MENU_ITEMS_CLASSES_BY_DIRECTION_OPTION: { [direction: string]: string } = {
  'top-right': 'top-0 translate-y-[calc(-100%-8px)]',
  top: 'top-0 left-[50%] translate-x-[-50%] translate-y-[calc(-100%-8px)]',
  'top-left': 'top-0 right-0 translate-y-[calc(-100%-8px)]',
  'bottom-right': 'left-0 bottom-0 translate-y-[calc(100%+8px)]',
  bottom: 'bottom-0 left-[50%] translate-x-[-50%] translate-y-[calc(100%+8px)]',
  'bottom-left': 'right-0 bottom-0 translate-y-[calc(100%+8px)]',
};

export function ChatLayoutConfigButton({
  buttonText,
  icon = 'ellipsis-v',
  direction = 'bottom-right',
  size = 'md',
  multicolumnEnabled,
}: {
  // HACK: In order for this to fit in with speed audit, need a way to add legacy buttons
  buttonText?: string;
  icon?: FontAwesomeIconProps['icon'];
  direction?: string;
  size?: 'sm' | 'md' | 'lg';
  multicolumnEnabled?: boolean;
}) {
  const { columnWidth, setColumnWidth, layout, setLayout } = useChatLayoutStore();

  const minColumnWidth = 300;
  const maxColumnWidth = 1200;
  const a = (maxColumnWidth - minColumnWidth) / 100;
  const b = minColumnWidth;

  const buttons = React.useMemo(
    () => [
      { id: 'default', label: 'Default' },
      { id: 'inline', label: ChatLayout.Inline },
      ...(multicolumnEnabled ? [{ id: 'multicolumn', label: ChatLayout.MultiColumn }] : []),
    ],
    [multicolumnEnabled],
  );

  return (
    <Menu as="div" className={cn('scaleui relative inline-block text-left h-fit')}>
      {({ open }) => (
        <>
          <Menu.Button className="rounded-full hover:bg-opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-opacity-75">
            {buttonText ? (
              <Button>{buttonText}</Button>
            ) : (
              <Icon
                icon={icon}
                size={size}
                hasBackground={open}
                rounded
                tooltip="Show display options"
              />
            )}
          </Menu.Button>
          <Transition
            as={React.Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items
              className={twMerge(
                'absolute overflow-hidden divide-y rounded-md shadow-lg bg-neutral-0 divide-neutral-100 ring-1 ring-neutral-1000 ring-opacity-5 focus:outline-none z-50',
                MENU_ITEMS_CLASSES_BY_DIRECTION_OPTION[direction],
              )}
            >
              <Menu.Item>
                <div className="p-4">
                  <Typography.Subtitle3 className="mb-2">Task Layout</Typography.Subtitle3>
                  <SegmentedButtons
                    buttons={buttons}
                    onSelect={v => setLayout(v === 'default' ? null : (v as ChatLayout))}
                    selected={layout || 'default'}
                  />
                </div>
              </Menu.Item>
              <Menu.Item>
                <div className="p-4">
                  <Typography.Subtitle3>Column Width</Typography.Subtitle3>
                  <Slider
                    label="Column Width"
                    value={(columnWidth - b) / a}
                    setValue={v => setColumnWidth(v * a + b)}
                  />
                </div>
              </Menu.Item>
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
}
